import { useEffect, useMemo, useState } from 'react';
import { MortalityCalculator, type CalculationResult, type DebitCondition, type Gender, type MortalityData } from '@mortality/core';

import { deriveRiskLevel, referralDisclaimer } from '../lib/riskLevel';
import type { CalculationRecord } from '../types';
import { ResultCard } from './ResultCard';

const MANUAL_ADJUSTMENT = 100;

interface CalculatorPanelProps {
  data: MortalityData | null;
  loading: boolean;
  onSave: (calculation: Omit<CalculationResult, 'id' | 'timestamp'>) => Promise<void>;
}

const genderOptions: Array<{ label: string; value: Gender }> = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
];

export function CalculatorPanel({ data, loading, onSave }: CalculatorPanelProps) {
  const [age, setAge] = useState('');
  const [gender, setGender] = useState<Gender>('male');
  const [search, setSearch] = useState('');
  const [selectedConditions, setSelectedConditions] = useState<DebitCondition[]>([]);
  const [result, setResult] = useState<CalculationRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (!data) {
      setSelectedConditions([]);
    }
  }, [data]);

  const calculator = useMemo(() => {
    const calc = new MortalityCalculator();
    if (data) {
      calc.setData(data.debitColumns, data.ageMin, data.ageMax, data.matrices);
    }
    return calc;
  }, [data]);

  const sortedCatalog = useMemo(() => {
    if (!data) {
      return [] as DebitCondition[];
    }

    return [...data.debitCatalog].sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const selectedIds = useMemo(() => new Set(selectedConditions.map(condition => condition.id)), [selectedConditions]);

  const filteredConditions = useMemo(() => {
    const term = search.trim().toLowerCase();

    return sortedCatalog.filter(condition => {
      const matchesTerm = term
        ? condition.name.toLowerCase().includes(term) || condition.notes.toLowerCase().includes(term)
        : true;
      return matchesTerm;
    });
  }, [search, sortedCatalog]);

  const hasReferralCondition = selectedConditions.some(condition => condition.requiresReferral);
  const totalDebits = selectedConditions.reduce((sum, condition) => {
    if (condition.requiresReferral) {
      return sum;
    }
    return sum + condition.debits;
  }, 0);
  const effectiveDebits = Math.max(0, totalDebits + MANUAL_ADJUSTMENT);
  const referralNote = referralDisclaimer(hasReferralCondition);

  const mostSignificantCondition = useMemo(() => {
    if (selectedConditions.length === 0) {
      return null;
    }

    const referral = selectedConditions.find(condition => condition.requiresReferral);
    if (referral) {
      return referral.name;
    }

    return selectedConditions.reduce<DebitCondition | null>((current, candidate) => {
      if (!current || candidate.debits > current.debits) {
        return candidate;
      }
      return current;
    }, null)?.name ?? null;
  }, [selectedConditions]);

  const toggleCondition = (condition: DebitCondition) => {
    setSelectedConditions(prev => {
      if (prev.some(item => item.id === condition.id)) {
        return prev.filter(item => item.id !== condition.id);
      }
      return [...prev, condition];
    });
  };

  const resetForm = () => {
    setAge('');
    setGender('male');
    setSearch('');
    setSelectedConditions([]);
    setResult(null);
    setError(null);
  };

  const handleCalculate = async () => {
    setError(null);

    if (!data) {
      setError('Load or upload mortality tables before calculating.');
      return;
    }

    const ageNumber = Number(age);
    if (Number.isNaN(ageNumber) || ageNumber < data.ageMin || ageNumber > data.ageMax) {
      setError(`Enter an age between ${data.ageMin} and ${data.ageMax}.`);
      return;
    }

    if (selectedConditions.length === 0) {
      setError('Select at least one condition from the catalog.');
      return;
    }

    const calcResult = calculator.calculateMonths(gender, ageNumber, selectedConditions, MANUAL_ADJUSTMENT);

    if (calcResult.months == null) {
      setError('Unable to calculate with the current selections.');
      return;
    }

    const months = calcResult.months;
    const years = months / 12;
    const expectedAge = ageNumber + years;
    const payload: Omit<CalculationResult, 'id' | 'timestamp'> = {
      age: ageNumber,
      gender,
      selectedConditions,
      mostSignificantCondition,
      manualAdjustment: MANUAL_ADJUSTMENT,
      totalDebits,
      effectiveDebits: calcResult.effectiveDebits,
      riskLevel: deriveRiskLevel(calcResult.effectiveDebits, calcResult.requiresReferral),
      months,
      years,
      expectedAge,
      usedColumn: calcResult.usedColumn,
    };

    const entry: CalculationRecord = {
      id: `calc-${Date.now()}`,
      timestamp: new Date(),
      requiresReferral: calcResult.requiresReferral,
      ...payload,
      months,
      years,
      expectedAge,
    };

    try {
      setSaving(true);
      await onSave(payload);
      setResult(entry);
    } catch (saveError) {
      console.error('Failed to save calculation', saveError);
      setError(saveError instanceof Error ? saveError.message : 'Unable to save calculation right now.');
    } finally {
      setSaving(false);
    }
  };

  const disableCalculate = !data || loading || saving;

  return (
    <section className="panel full-width calculator-panel" aria-labelledby="calculator-heading">
      <h2 id="calculator-heading" className="section-title">Mortality Calculator</h2>
      <div className="calculator-layout">
        <div className="calculator-left">
          <div className="input-group">
            <label htmlFor="age-input">Current age</label>
            <input
              id="age-input"
              type="number"
              inputMode="decimal"
              placeholder="Enter age"
              value={age}
              min={data?.ageMin ?? 0}
              max={data?.ageMax ?? 120}
              onChange={event => setAge(event.target.value)}
            />
          </div>
          <div className="input-group">
            <label>Gender</label>
            <div className="gender-toggle" role="group" aria-label="Gender">
              {genderOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={option.value === gender ? 'active' : ''}
                  onClick={() => setGender(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="calculator-actions">
            <button className="button-primary" type="button" onClick={handleCalculate} disabled={disableCalculate}>
              {loading ? 'Loading data…' : saving ? 'Saving…' : 'Calculate outlook'}
            </button>
            <button className="button-secondary" type="button" onClick={resetForm}>
              Reset form
            </button>
          </div>
          <div className="status-card">
            <strong>Summary</strong>
            <p className="muted">
              {selectedConditions.length} condition{selectedConditions.length === 1 ? '' : 's'} selected.
            </p>
            {referralNote ? <p className="muted" style={{ color: '#b45309' }}>{referralNote}</p> : null}
          </div>

          {error ? (
            <p className="muted" style={{ color: '#b91c1c', marginTop: '0.5rem' }}>{error}</p>
          ) : null}

          {result ? <div style={{ marginTop: '1.5rem' }}><ResultCard record={result} referralNote={referralNote} /></div> : null}
        </div>
        <div className="calculator-right">
          <div className="conditions">
            <div className="conditions-search">
              <input
                className="condition-search-input"
                type="search"
                placeholder="Search conditions or notes"
                value={search}
                onChange={event => setSearch(event.target.value)}
                aria-label="Search conditions"
              />
            </div>
        <div className="conditions-list" aria-live="polite">
          {!data && filteredConditions.length === 0 ? (
            <p className="muted">Upload or reload mortality tables to browse conditions.</p>
          ) : (
            filteredConditions.map(condition => {
              const isSelected = selectedIds.has(condition.id);
              const buttonLabel = isSelected ? 'Remove' : 'Add';
              return (
                <article className={`condition-card ${isSelected ? 'selected-card' : ''}`} key={condition.id}>
                  <div>
                    <div className="condition-card-title">
                      <h4>{condition.name}</h4>
                      {condition.requiresReferral ? <span className="condition-badge">Referral</span> : null}
                    </div>
                    {condition.notes ? <p className="muted">{condition.notes}</p> : null}
                  </div>
                  <button
                    type="button"
                    className={isSelected ? 'remove' : ''}
                    onClick={() => toggleCondition(condition)}
                  >
                    {buttonLabel}
                  </button>
                </article>
              );
            })
          )}
        </div>
          </div>
          <div className="selected-conditions" aria-live="polite">
            {selectedConditions.map(condition => (
              <span className="pill" key={condition.id}>
                {condition.name}
                <button type="button" onClick={() => toggleCondition(condition)} aria-label={`Remove ${condition.name}`}>
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
